import type { Extension, Questionnaire } from "@medplum/fhirtypes";
import { catalogModelKindTag, catalogNameToFhir, PERFORMANCE_MEASURE_DEFINITION_KIND } from "@repo/api";
import type { DefineQuestionnaireDefinitionInput } from "@repo/api/schemas";
import { TOTAL_SCORE_ID } from "@repo/domain";
import {
  type EnablementCondition,
  encodeQuestionnaireMarks,
  encodeQuestionnaireSection,
  QUESTIONNAIRE_DEFINITION_PROFILE,
  QUESTIONNAIRE_MEMBER_MARKS_URL,
  QUESTIONNAIRE_SCORES_URL,
  QUESTIONNAIRE_TOTAL_BANDS_URL,
  type QuestionnaireItem,
  type QuestionnaireSection,
} from "../fhir/questionnaire-definition-storage";
import { deriveQuestionnaireFields } from "../fhir/questionnaire-derived";
import type { QuestionnaireRow } from "../fhir/types";
import type { QuestionnaireDefinitionDto } from "../views/questionnaire-definition";

// ============================================================
// The optimistic row for a questionnaire definition save
//
// It must emit exactly what `questionnaire-definition.codec.ts` emits, because
// the authoritative row replaces this one and any difference reads as a
// flicker. NO DTO IS SMUGGLED ONTO THE ROW: the optimistic write builds a
// genuine FHIR `Questionnaire` and the projection reads it back through
// exactly the path a synced row takes — one projection, not two.
// ============================================================

type InputSection = DefineQuestionnaireDefinitionInput["sections"][number];
type InputItem = InputSection["items"][number];
type InputCondition = NonNullable<InputSection["conditions"]>[number];
type InputReference = Extract<InputCondition, { question: unknown }>["question"];

export function buildOptimisticQuestionnaireSections(sections: readonly InputSection[]): QuestionnaireSection[] {
  return sections.map((section) => {
    const conditions = optimisticConditions(section.conditions);
    return {
      id: optimisticMemberId(section.id, section.clientRef),
      label: section.label,
      ...(section.showTogether ? { showTogether: true } : {}),
      ...(conditions === undefined ? {} : { conditions }),
      ...(section.conditionBehavior === undefined ? {} : { conditionBehavior: section.conditionBehavior }),
      items: section.items.map(optimisticItem),
    };
  });
}

function optimisticItem(item: InputItem): QuestionnaireItem {
  if (item.kind === "Display") {
    return { id: optimisticMemberId(item.id, item.clientRef), kind: "Display", text: item.text };
  }
  if (item.kind === "RepeatingGroup") {
    return {
      id: optimisticMemberId(item.id, item.clientRef),
      kind: "RepeatingGroup",
      label: item.label,
      minOccurrences: item.minOccurrences,
      items: item.items.map(optimisticItem) as (
        | Extract<QuestionnaireItem, { kind: "Question" }>
        | Extract<QuestionnaireItem, { kind: "Display" }>
      )[],
    };
  }
  const conditions = optimisticConditions(item.conditions);
  return {
    id: optimisticMemberId(item.id, item.clientRef),
    kind: "Question",
    ...(item.help === undefined ? {} : { help: item.help }),
    label: item.label,
    required: item.required,
    ...(item.askYesNoFirst ? { askYesNoFirst: true } : {}),
    ...(conditions === undefined ? {} : { conditions }),
    ...(item.conditionBehavior === undefined ? {} : { conditionBehavior: item.conditionBehavior }),
    value:
      item.value.kind === "Choice"
        ? {
            kind: "Choice",
            cardinality: item.value.cardinality,
            allowFreeText: item.value.allowFreeText,
            options: item.value.options.map((option) => ({
              id: optimisticMemberId(option.id, option.clientRef),
              label: option.label,
              ...(option.detail === undefined ? {} : { detail: option.detail }),
              ...(option.points === undefined ? {} : { points: option.points }),
            })),
          }
        : item.value,
  };
}

function optimisticConditions(conditions: readonly InputCondition[] | undefined): EnablementCondition[] | undefined {
  return conditions?.map((condition) =>
    "fact" in condition
      ? condition
      : {
          operator: condition.operator,
          question: optimisticReference(condition.question),
          value:
            condition.value.kind === "Choice"
              ? { kind: "Choice", optionId: optimisticReference(condition.value.option) }
              : condition.value,
        },
  );
}

function optimisticReference(reference: InputReference): string {
  return reference.kind === "Persisted" ? reference.id : optimisticMemberId(undefined, reference.clientRef);
}

function optimisticMemberId(id: string | undefined, clientRef: string | undefined): string {
  if (id) return id;
  if (!clientRef) throw new Error("A new questionnaire member requires a temporary client reference");
  return `optimistic-member-${clientRef}`;
}

function optimisticId(url: string, version: number): string {
  let hash = 2166136261;
  for (const character of url) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return `optimistic-questionnaire-${(hash >>> 0).toString(36)}-${version}`;
}

export function optimisticQuestionnaireDefinitionRow(
  dto: QuestionnaireDefinitionDto,
  prior?: QuestionnaireRow,
  performance = false,
): QuestionnaireRow {
  const totalBands = dto.scores.find((score) => score.id === TOTAL_SCORE_ID)?.bands;
  // Rebuilt over the stored array, not replacing it: `extension` is one FHIR
  // field, so writing only ours would drop whatever another writer put there.
  const extension: Extension[] = [
    ...(prior?.extension ?? []).filter(
      (candidate) =>
        candidate.url !== QUESTIONNAIRE_MEMBER_MARKS_URL &&
        candidate.url !== QUESTIONNAIRE_SCORES_URL &&
        candidate.url !== QUESTIONNAIRE_TOTAL_BANDS_URL,
    ),
    encodeQuestionnaireMarks(dto.marks),
    {
      url: QUESTIONNAIRE_SCORES_URL,
      valueString: JSON.stringify(dto.scores.filter((score) => score.id !== TOTAL_SCORE_ID)),
    },
    ...(totalBands?.length ? [{ url: QUESTIONNAIRE_TOTAL_BANDS_URL, valueString: JSON.stringify(totalBands) }] : []),
  ];
  const resource: Questionnaire = {
    resourceType: "Questionnaire",
    meta: {
      ...prior?.meta,
      profile: performance ? [] : [QUESTIONNAIRE_DEFINITION_PROFILE],
      ...(performance ? { tag: [catalogModelKindTag(PERFORMANCE_MEASURE_DEFINITION_KIND)] } : {}),
    },
    status: dto.availability === "Active" ? "active" : "retired",
    url: dto.url,
    version: String(dto.version),
    date: dto.mintedAt,
    ...catalogNameToFhir(dto.name),
    ...(dto.description === undefined ? {} : { description: dto.description }),
    item: dto.sections.map(encodeQuestionnaireSection),
    extension,
  };
  return {
    ...resource,
    id: prior?.id ?? optimisticId(dto.url, dto.version),
    ...deriveQuestionnaireFields(resource),
  };
}

/**
 * Total bands ride on a placeholder Total entry: the row writer stores them in
 * their own extension, and the read side derives the real Total from the
 * sections, so the placeholder's empty item list is never read.
 */
export function buildOptimisticQuestionnaireScores(
  scores: NonNullable<DefineQuestionnaireDefinitionInput["scores"]>,
  totalBands?: readonly { label: string; from: number }[],
): QuestionnaireDefinitionDto["scores"] {
  return [
    ...(totalBands?.length ? [{ id: TOTAL_SCORE_ID, label: "Total", itemIds: [], bands: totalBands }] : []),
    ...scores.map((score, index) => ({
      id: score.id ?? `optimistic-score-${score.clientRef ?? index}`,
      label: score.label,
      itemIds: score.items.map(optimisticReference),
      ...(score.bands?.length ? { bands: score.bands } : {}),
      ...(score.calculation ? { calculation: score.calculation } : {}),
    })),
  ];
}
